<template>
    <tr data-component="OrdersListOrder"
        class="order-search-result"
        :class="{'order-search-result--cancelled': isCancelled}">
        <!-- The @click handlers are added on each <td> because we only want to trigger it if you click on the row
        itself and not a child element in the row (like a button). But we can't do that on the <tr> because you never
        actually click directly on a <tr>, only on a <td>. -->

        <!-- Order booking ref and staff ref cell (mobile only) -->
        <td class="mobile-only"
            @click.prevent="viewOrder()">
            {{ order.bookingRef }}

            <!-- Staff ref / table number for F&B orders (mobile only) -->
            <span v-if="order.staffRef"
                  class="dt-table__cell-subtitle">
                {{ order.staffRef }}
            </span>

            <!-- Collection number for F&B orders (mobile only) -->
            <span v-if="order.displayCollectionNumber"
                  class="dt-table__cell-subtitle">
                {{ order.displayCollectionNumber }}
            </span>

            <!-- Order total is shown here in the cell on desktop. On mobile it goes into a separate cell -->
            <span class="dt-table__cell-subtitle desktop-only">
                {{ currencyFilter(order.getTotal()) }}
            </span>
        </td>

        <td @click.prevent="viewOrder()">
            <span class="desktop-only">
                {{ order.bookingRef }}
            </span>

            <!-- Staff ref / table number for F&B orders (desktop only) -->
            <span v-if="order.staffRef"
                  class="dt-table__cell-subtitle desktop-only">
                {{ order.staffRef }}
            </span>

            <!-- Collection number for F&B orders (desktop only) -->
            <span v-if="order.displayCollectionNumber"
                  class="dt-table__cell-subtitle desktop-only">
                {{ order.displayCollectionNumber }}
            </span>

            <!-- Order total is shown here in the cell on desktop. On mobile it goes into a separate cell -->
            <span class="dt-table__cell-subtitle">
                <i class="fa-solid fa-coins fa-fw mobile-only"></i>
                {{ currencyFilter(order.getTotal()) }}
            </span>

            <span class="label-list">
                <span v-if="isVoided"
                      class="label label-cancelled">{{ l('ORDER_STATUS.VOIDED') }}</span>
                <span v-else-if="isCancelled"
                      class="label label-cancelled">{{ l('ORDER_STATUS.CANCELLED') }}</span>
                <span v-else-if="isUnconfirmed"
                      class="label label-danger">
                    <i class="fa-solid fa-exclamation-triangle"></i> {{ l('ORDER_STATUS.UNCONFIRMED') }}
                </span>

                <OrderPaidStatusLabel :order="order" />

                <OrderGroupBookingLabel :order="order" />

                <FulfillmentStatusLabel v-if="order.containsKitchenItems()"
                                        :fulfillments="order.fulfillments" />
            </span>
        </td>

        <!-- Order date cell -->
        <td @click.prevent="viewOrder()">
            <i class="fa-solid fa-calendar fa-fw mobile-only"></i>
            <span class="no-wrap">{{ dateFilter(order.date) }}</span>&nbsp;
            <br class="desktop-only" />
            <span class="no-wrap">{{ timeFilter(order.date) }}</span>

            <!-- Display the branch that created the order if it's different from the current order branch. -->
            <span v-if="order.orderBranch && showOrderBranchName"
                  class="dt-table__cell-subtitle desktop-only no-wrap">
                <i class="fa-solid fa-shop"></i> {{ order.orderBranch.name }}
            </span>
        </td>

        <!-- Order customer and memberships cell -->
        <td :class="{'dt-table__cell--empty': !hasCustomerOrMembershipData}"
            @click.prevent="viewOrder()">
            <CustomerInfo :customer="order.customer"
                          class="list-customer flex--grow no-overflow"></CustomerInfo>

            <OrdersListMembership v-for="(membership, index) in order.memberships"
                                  :key="membership.ID"
                                  :class="{first: index === 0}"
                                  :membership="membership"
                                  @click.prevent.stop="viewMembership(membership)" />
        </td>

        <!-- Redeem button cell -->
        <td v-if="redeemSession">
            <OrdersListRedeemSessionBtn v-if="isLive"
                                        :order="order"
                                        :session-with-event="redeemSession" />
        </td>

        <!-- Button cell -->
        <td class="dt-table__cell--last dt-table__btn-cell desktop-only">
            <button class="btn btn-lg btn-info dt-table__view-btn"
                    @click.prevent.stop="viewOrder()">
                <i class="fa-solid fa-arrow-right"></i>
            </button>
        </td>
    </tr>
</template>

<script>
import CustomerInfo from '@/epos/vue/components/Customers/CustomerInfo';
import FulfillmentStatusLabel from '@/vue/components/Badges/FulfillmentStatusLabel';
import OrderGroupBookingLabel from '@/vue/components/Badges/OrderGroupBookingLabel';
import OrderPaidStatusLabel from '@/vue/components/Badges/OrderPaidStatusLabel';
import OrdersListMembership from './OrdersListMembership';
import OrdersListRedeemSessionBtn from './OrdersListRedeemSessionBtn';
import { container } from '@/container';
import { mapState } from 'vuex';

export default {
    components: {
        FulfillmentStatusLabel,
        OrderGroupBookingLabel,
        OrderPaidStatusLabel,
        OrdersListRedeemSessionBtn,
        OrdersListMembership,
        CustomerInfo,
    },

    props: {
        order: {
            type: Object,
            required: true,
        },

        /**
         * @type {?SessionWithEvent}
         */
        redeemSession: {
            type: Object,
            default: null,
        },
    },

    computed: {
        ...mapState(['currentBranch', 'currentUser']),

        showOrderBranchName() {
            return this.currentUser.company.settings.enableCrossBranchSelling
                || (this.order.orderBranch && this.order.orderBranch.ID !== this.currentBranch.ID);
        },

        hasCustomerOrMembershipData() {
            return !(this.order.customer && !this.order.customer.hasData())
                || this.order.memberships.length > 0;
        },

        isCancelled() {
            return this.order.isCancelled();
        },

        isUnconfirmed() {
            return !this.order.isConfirmed();
        },

        isVoided() {
            return this.order.isVoided();
        },

        isLive() {
            return this.order.isLive();
        },
    },

    methods: {
        /**
         * @param {Membership} membership
         */
        viewMembership(membership) {
            container.navigationService.viewMembershipDetails(
                membership.ref,
                membership.highlightMemberRef,
                true,
            );
        },

        viewOrder() {
            container.navigationService.viewOrderDetails(this.order.bookingRef);
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";

.order-search-result {
    .dt-table__btn-cell {
        display: flex;
        justify-content: stretch;
    }

    .btn {
        height: 52px;
    }

    &--cancelled {
        text-decoration: line-through;
    }
}

@media (max-width: @mobile-max) {
    .order-search-result {
        .dt-table__cell-subtitle {
            display: inline-block;
            color: @dt-dark-grey;
            font-size: 15px;
            margin-right: @half-gutter-width;
        }

        .mobile-only {
            .dt-table__cell-subtitle {
                display: block;
                color: @dt-light-grey;
            }
        }
    }

    .dt-table__flex-mobile {
        tr {
            td {
                &:nth-child(2) {
                    order: 3;
                }

                &:nth-child(3) {
                    order: 2;
                }

                &:nth-child(4) {
                    order: 4;
                }

                &:nth-child(5) {
                    order: 5;
                }
            }
        }
    }
}

@media (min-width: @non-mobile-min) {
    .order-search-result {
        &--cancelled {
            // On desktop the colour of the line through the text is black by default.
            // Make it a white line so you can see it.
            color: @white;
        }
    }
}

</style>
